import React from 'react';
import Tag from '@ui/tag/Tag';
import { PreferredTierBadge }
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/DisplayMarketStateItemPrice';
import { BookingPositionHotel } from './model/BookingPositionState';

// Screen Parts of the Booking ranking page: the card frame, the card head, the hotel name cell, the
// badge key and the key list. They use the markup of the sibling pages (Promotions, Channel check).

export const RankingCard: React.FunctionComponent<React.PropsWithChildren<{ className?: string }>> = ({ className, children }) =>
	<section className={`overflow-hidden rounded-lg bg-white shadow ${className ?? ''}`}>{children}</section>;

export const RankingCardHead: React.FunctionComponent<{
	title: React.ReactNode,
	subtitle?: React.ReactNode,
	right?: React.ReactNode,
}> = ({ title, subtitle, right }) =>
	<div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-b border-gray-200 px-4 py-4 sm:px-5">
		<div className="min-w-0 max-w-full">
			<h2 className="inline text-base font-semibold text-gray-900">{title}</h2>
			{subtitle !== undefined && <span className="ml-0 block text-xs text-gray-500 sm:ml-3 sm:inline">{subtitle}</span>}
		</div>
		{right !== undefined && <div className="flex min-w-0 max-w-full flex-wrap items-center justify-start gap-2 sm:ml-auto sm:justify-end">{right}</div>}
	</div>;

// The name takes the free width. "You" (Tag info) for the own hotel. The Preferred badge sits in a fixed
// 16 px slot at the end, so the badges of all rows line up (PreferredTierBadge, as NightStrip).
export const HotelNameCell: React.FunctionComponent<{ hotel: BookingPositionHotel, sub?: React.ReactNode }> = ({ hotel, sub }) =>
	<div className="flex min-w-0 flex-col gap-0.5">
		<span className="flex min-w-0 items-center gap-2">
			<span className={`min-w-0 flex-1 text-sm ${hotel.isMain ? 'font-semibold text-blue-700' : 'font-medium text-gray-900'}`}>
				{hotel.name}
			</span>
			{hotel.isMain && <Tag state="info">You</Tag>}
			<span className="flex w-4 shrink-0 justify-center">
				{hotel.preferredTier !== null && <PreferredTierBadge plus={hotel.preferredTier === 'PREFERRED_PLUS'} />}
			</span>
		</span>
		{sub !== undefined && <span className="text-xs text-gray-500 sm:hidden">{sub}</span>}
	</div>;

export const BadgeKey: React.FunctionComponent = () =>
	<div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-gray-600">
		<span className="inline-flex items-center gap-1.5"><PreferredTierBadge plus={false} />Booking.com Preferred partner</span>
		<span className="inline-flex items-center gap-1.5"><PreferredTierBadge plus />Preferred Plus</span>
	</div>;

export type KeyItem = { term?: string, text: string };

export const KeyList: React.FunctionComponent<{ items: KeyItem[] }> = ({ items }) =>
	<ul className="flex list-disc flex-col gap-0.5 pl-4 text-xs leading-5 text-gray-600">
		{items.map((item) => <li key={item.term ?? item.text}>
			{item.term !== undefined && <span className="font-semibold text-gray-700">{item.term}</span>}
			{item.term !== undefined ? ': ' : ''}{item.text}
		</li>)}
	</ul>;

export const TH = 'whitespace-nowrap px-2.5 py-2.5 text-left text-xs font-semibold text-gray-500';
export const TD = 'px-2.5 py-3 text-sm text-gray-900';
